<template>
	<div class="user-info-container">
		<div class="user-card">
			<div class="user-header">
				<div class="avatar-section">
					<el-avatar 
						:size="80" 
						:src="userInfo.Avatar"
						class="user-avatar"
					>
						<i class="el-icon-user-solid"></i>
					</el-avatar>
					<div class="status-badge" v-if="isOnline">
						<span class="status-dot"></span>
					</div>
				</div>
				<div class="user-details">
					<h2 class="user-name">{{ userInfo.NickName || userInfo.Account || '用户' }}</h2>
					<p class="user-account">{{ userInfo.Account }}</p>
				</div>
			</div>

			<div class="user-body">
				<div class="info-item" v-if="userInfo.Email">
					<i class="el-icon-message info-icon"></i>
					<span class="info-label">邮箱：</span>
					<span class="info-value">{{ userInfo.Email }}</span>
				</div>

				<div class="info-item" v-if="userInfo.CompanyName">
					<i class="el-icon-office-building info-icon"></i>
					<span class="info-label">公司：</span>
					<span class="info-value">{{ userInfo.CompanyName }}</span>
				</div>

				<div class="info-item" v-if="userInfo.CompanyCode">
					<i class="el-icon-document info-icon"></i>
					<span class="info-label">公司代码：</span>
					<span class="info-value">{{ userInfo.CompanyCode }}</span>
				</div>

				<div class="info-item" v-if="userInfo.RoleID">
					<i class="el-icon-user info-icon"></i>
					<span class="info-label">角色ID：</span>
					<span class="info-value">{{ userInfo.RoleID }}</span>
				</div>
			</div>

			<div class="user-footer">
				<el-button 
					type="danger" 
					@click="handleLogout"
					:loading="logoutLoading"
					class="logout-button"
				>
					<i class="el-icon-switch-button"></i>
					<span>退出登录</span>
				</el-button>
			</div>
		</div>

		<!-- 令牌信息卡片（开发环境显示） -->
		<div class="token-card" v-if="showTokenInfo && tokenInfo">
			<div class="card-header">
				<h3>令牌信息</h3>
				<el-button 
					type="text"
					@click="showTokenInfo = false"
					class="close-button"
				>
					<i class="el-icon-close"></i>
				</el-button>
			</div>
			<div class="token-content">
				<div class="token-item">
					<span class="token-label">访问令牌：</span>
					<el-input 
						:value="tokenInfo.accessToken" 
						readonly
						class="token-input"
					>
						<template #append>
							<el-button @click="copyToken(tokenInfo.accessToken)">
								复制
							</el-button>
						</template>
					</el-input>
				</div>
				<div class="token-item" v-if="tokenInfo.expiresAt">
					<span class="token-label">过期时间：</span>
					<span class="token-value">{{ formatDate(tokenInfo.expiresAt) }}</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { signoutRedirect, getUser, getAccessToken } from '@/utils/oidcConfig'
import { getAccountInfo, clearLoginInfo } from '@/utils/localdatas'

export default {
	name: 'UserInfo',
	data() {
		return {
			userInfo: {},
			logoutLoading: false,
			isOnline: true,
			showTokenInfo: false,
			tokenInfo: null
		}
	},
	created() {
		this.loadUserInfo()
	},
	methods: {
		async loadUserInfo() {
			// 从本地存储加载用户信息
			const accountInfo = getAccountInfo()
			if (accountInfo) {
				this.userInfo = accountInfo
			}

			// 尝试从OIDC获取最新用户信息
			try {
				const user = await getUser()
				if (user && !user.expired) {
					// 更新用户信息
					const profile = user.profile || {}
					this.userInfo = {
						...this.userInfo,
						NickName: profile.name || profile.nickname || this.userInfo.NickName,
						Avatar: profile.picture || profile.avatar || this.userInfo.Avatar,
						Email: profile.email || this.userInfo.Email
					}

					// 获取令牌信息（仅开发环境）
					if (process.env.NODE_ENV === 'development') {
						const accessToken = await getAccessToken()
						if (accessToken) {
							this.tokenInfo = {
								accessToken: accessToken.substring(0, 50) + '...',
								fullToken: accessToken,
								expiresAt: user.expires_at
							}
							this.showTokenInfo = true
						}
					}
				} else {
					this.isOnline = false
				}
			} catch (error) {
				console.error('获取用户信息失败:', error)
				this.isOnline = false
			}
		},

		async handleLogout() {
			try {
				this.logoutLoading = true
				
				// 清除本地登录信息
				clearLoginInfo()
				
				// 执行OIDC登出
				await signoutRedirect()
			} catch (error) {
				console.error('登出失败:', error)
				this.$message.error('登出失败，请重试')
				this.logoutLoading = false
			}
		},

		copyToken(token) {
			if (this.tokenInfo && this.tokenInfo.fullToken) {
				const textarea = document.createElement('textarea')
				textarea.value = this.tokenInfo.fullToken
				document.body.appendChild(textarea)
				textarea.select()
				document.execCommand('copy')
				document.body.removeChild(textarea)
				this.$message.success('令牌已复制到剪贴板')
			}
		},

		formatDate(timestamp) {
			if (!timestamp) return '-'
			const date = new Date(timestamp * 1000)
			return date.toLocaleString('zh-CN')
		}
	}
}
</script>

<style lang="scss" scoped>
.user-info-container {
	padding: 20px;
	max-width: 800px;
	margin: 0 auto;
}

.user-card {
	background: #fff;
	border-radius: 16px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
	overflow: hidden;
	margin-bottom: 20px;
}

.user-header {
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	padding: 40px 30px;
	display: flex;
	align-items: center;
	gap: 20px;
	color: #fff;
}

.avatar-section {
	position: relative;
}

.user-avatar {
	border: 4px solid rgba(255, 255, 255, 0.3);
	background: rgba(255, 255, 255, 0.2);
}

.status-badge {
	position: absolute;
	bottom: 0;
	right: 0;
	width: 24px;
	height: 24px;
	background: #fff;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 3px solid #667eea;
}

.status-dot {
	width: 12px;
	height: 12px;
	background: #67c23a;
	border-radius: 50%;
	display: block;
}

.user-details {
	flex: 1;
}

.user-name {
	font-size: 24px;
	font-weight: 600;
	margin: 0 0 8px 0;
	color: #fff;
}

.user-account {
	font-size: 14px;
	margin: 0;
	opacity: 0.9;
	color: #fff;
}

.user-body {
	padding: 30px;
}

.info-item {
	display: flex;
	align-items: center;
	padding: 15px 0;
	border-bottom: 1px solid #f0f0f0;

	&:last-child {
		border-bottom: none;
	}
}

.info-icon {
	font-size: 20px;
	color: #667eea;
	margin-right: 12px;
	width: 24px;
}

.info-label {
	font-size: 14px;
	color: #666;
	min-width: 100px;
}

.info-value {
	font-size: 14px;
	color: #333;
	font-weight: 500;
	flex: 1;
}

.user-footer {
	padding: 20px 30px;
	background: #f8f9fa;
	border-top: 1px solid #e9ecef;
}

.logout-button {
	width: 100%;
	height: 44px;
	font-size: 16px;
	border-radius: 8px;
}

.token-card {
	background: #fff;
	border-radius: 16px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
	padding: 20px;
}

.card-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 20px;

	h3 {
		margin: 0;
		font-size: 18px;
		color: #333;
	}
}

.token-content {
	display: flex;
	flex-direction: column;
	gap: 15px;
}

.token-item {
	display: flex;
	align-items: center;
	gap: 10px;
}

.token-label {
	font-size: 14px;
	color: #666;
	min-width: 100px;
}

.token-input {
	flex: 1;
}

.token-value {
	font-size: 14px;
	color: #333;
}

/* 响应式设计 */
@media (max-width: 768px) {
	.user-header {
		flex-direction: column;
		text-align: center;
		padding: 30px 20px;
	}

	.user-name {
		font-size: 20px;
	}

	.user-body {
		padding: 20px;
	}

	.info-item {
		flex-direction: column;
		align-items: flex-start;
		gap: 8px;
	}

	.info-label {
		min-width: auto;
	}
}
</style>

